import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './AddActivityModal.css';

// =============================================================================
// TYPES
// =============================================================================

export interface AddActivityRequest {
    description: string;
    time?: string;
    period?: 'morning' | 'afternoon' | 'evening';
    category?: 'activity' | 'restaurant' | 'transport' | 'hotel';
}

interface Activity {
    id: string;
    time: string;
    title: string;
}

interface AddActivityModalProps {
    isOpen: boolean;
    onClose: () => void;
    tripId: string;
    dayIndex: number;
    cityName: string;
    existingActivities: Activity[];
    onAddActivity: (request: AddActivityRequest) => Promise<void>;
    isLoading?: boolean;
}

// =============================================================================
// QUICK SUGGESTIONS - MVP hardcoded based on context
// =============================================================================

interface Suggestion {
    icon: React.ReactNode;
    text: string;
    category: AddActivityRequest['category'];
    period?: AddActivityRequest['period'];
}

// SVG Icons for suggestions
const CoffeeIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/><line x1="6" x2="6" y1="2" y2="4"/><line x1="10" x2="10" y1="2" y2="4"/><line x1="14" x2="14" y1="2" y2="4"/>
    </svg>
);

const BreakfastIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12h20"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="m15 5-1 1"/><path d="M10 5.5V4"/><circle cx="12" cy="9" r="2"/>
    </svg>
);

const RestaurantIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>
    </svg>
);

const WineIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 22h8"/><path d="M7 10h10"/><path d="M12 15v7"/><path d="M12 15a5 5 0 0 0 5-5c0-2-.5-4-2-8H9c-1.5 4-2 6-2 8a5 5 0 0 0 5 5Z"/>
    </svg>
);

const MuseumIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 22h18"/><path d="M6 18v-7"/><path d="M10 18v-7"/><path d="M14 18v-7"/><path d="M18 18v-7"/><path d="m12 2 8 5H4l8-5z"/>
    </svg>
);

const SunsetIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 10V2"/><path d="m4.93 10.93 1.41 1.41"/><path d="M2 18h2"/><path d="M20 18h2"/><path d="m19.07 10.93-1.41 1.41"/><path d="M22 22H2"/><path d="m16 6-4 4-4-4"/><path d="M16 18a4 4 0 0 0-8 0"/>
    </svg>
);

const WalkIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="5" r="1"/><path d="m9 20 3-6 3 6"/><path d="m6 8 6 2 6-2"/>
    </svg>
);

function getQuickSuggestions(
    cityName: string,
    existingActivities: Activity[],
    _t: (key: string, options?: any) => string
): Suggestion[] {
    const suggestions: Suggestion[] = [];
    
    // Check what's missing in the day
    const times = existingActivities.map(a => parseInt(a.time?.split(':')[0] || '12'));
    const hasBreakfast = times.some(h => h >= 7 && h < 10);
    const hasLunch = times.some(h => h >= 12 && h < 14);
    const hasDinner = times.some(h => h >= 19 && h < 22);
    const hasCoffee = existingActivities.some(a => 
        a.title.toLowerCase().includes('coffee') || 
        a.title.toLowerCase().includes('cafe') ||
        a.title.toLowerCase().includes('cafea')
    );

    // Add contextual suggestions
    if (!hasBreakfast) {
        suggestions.push({
            icon: <BreakfastIcon />,
            text: _t('addActivity.suggestion_breakfast', { city: cityName }),
            category: 'restaurant',
            period: 'morning'
        });
    }

    if (!hasCoffee) {
        suggestions.push({
            icon: <CoffeeIcon />,
            text: _t('addActivity.suggestion_coffee'),
            category: 'restaurant',
            period: 'afternoon'
        });
    }

    if (!hasLunch) {
        suggestions.push({
            icon: <RestaurantIcon />,
            text: _t('addActivity.suggestion_lunch'),
            category: 'restaurant',
            period: 'afternoon'
        });
    }

    if (!hasDinner) {
        suggestions.push({
            icon: <WineIcon />,
            text: _t('addActivity.suggestion_dinner', { city: cityName }),
            category: 'restaurant',
            period: 'evening'
        });
    }

    // Always add some activity suggestions
    suggestions.push({
        icon: <MuseumIcon />,
        text: _t('addActivity.suggestion_museum'),
        category: 'activity',
        period: 'morning'
    });

    suggestions.push({
        icon: <SunsetIcon />,
        text: _t('addActivity.suggestion_viewpoint'),
        category: 'activity',
        period: 'evening'
    });

    suggestions.push({
        icon: <WalkIcon />,
        text: _t('addActivity.suggestion_walk', { city: cityName }),
        category: 'activity',
        period: 'morning'
    });

    // Return max 5 suggestions, prioritizing meals if missing
    return suggestions.slice(0, 5);
}

// =============================================================================
// TIME OPTIONS
// =============================================================================

const TIME_PERIODS = [
    { value: 'morning', labelKey: 'addActivity.morning', hours: '07:00 - 12:00' },
    { value: 'afternoon', labelKey: 'addActivity.afternoon', hours: '12:00 - 18:00' },
    { value: 'evening', labelKey: 'addActivity.evening', hours: '18:00 - 23:00' },
] as const;

const HOURS = [
    '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00',
    '19:00', '20:00', '21:00', '22:00'
];

// =============================================================================
// COMPONENT
// =============================================================================

export default function AddActivityModal({
    isOpen,
    onClose,
    dayIndex,
    cityName,
    existingActivities,
    onAddActivity,
    isLoading = false,
}: AddActivityModalProps) {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    
    // Form state
    const [description, setDescription] = useState('');
    const [showTimeOptions, setShowTimeOptions] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState<AddActivityRequest['period']>();
    const [selectedTime, setSelectedTime] = useState<string>();

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setDescription('');
            setShowTimeOptions(false);
            setSelectedPeriod(undefined);
            setSelectedTime(undefined);
            // Focus input after animation
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Close on ESC
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isLoading) onClose();
        };

        if (isOpen) {
            window.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose, isLoading]);

    // Get suggestions
    const suggestions = getQuickSuggestions(cityName, existingActivities, t);

    // Handle suggestion click
    const handleSuggestionClick = (suggestion: Suggestion) => {
        setDescription(suggestion.text);
        if (suggestion.period) {
            setSelectedPeriod(suggestion.period);
            setShowTimeOptions(true);
        }
    };

    // Handle submit
    const handleSubmit = async () => {
        if (!description.trim() || isLoading) return;

        await onAddActivity({
            description: description.trim(),
            time: selectedTime,
            period: selectedPeriod,
        });
    };

    // Handle Enter key
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="add-activity-overlay" onClick={() => !isLoading && onClose()}>
            <div className="add-activity-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="add-activity-header">
                    <div className="header-info">
                        <h3>{t('addActivity.title')}</h3>
                        <p>{t('addActivity.subtitle', { day: dayIndex + 1, city: cityName })}</p>
                    </div>
                    <button 
                        className="close-modal-btn" 
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Main Input */}
                <div className="add-activity-input-section">
                    <label className="input-label">
                        {t('addActivity.what_label')}
                    </label>
                    <div className="input-wrapper">
                        <input
                            ref={inputRef}
                            type="text"
                            className="activity-input"
                            placeholder={t('addActivity.placeholder')}
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isLoading}
                        />
                        <span className="input-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                        </span>
                    </div>
                </div>

                {/* Time Options (optional, collapsible) */}
                <div className="time-section">
                    <button 
                        className={`time-toggle ${showTimeOptions ? 'open' : ''}`}
                        onClick={() => setShowTimeOptions(!showTimeOptions)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                        </svg>
                        <span>{t('addActivity.time_optional')}</span>
                        <svg 
                            className="chevron" 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                        >
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>

                    {showTimeOptions && (
                        <div className="time-options">
                            <div className="period-pills">
                                {TIME_PERIODS.map(period => (
                                    <button
                                        key={period.value}
                                        className={`period-pill ${selectedPeriod === period.value ? 'active' : ''}`}
                                        onClick={() => setSelectedPeriod(
                                            selectedPeriod === period.value ? undefined : period.value as any
                                        )}
                                    >
                                        <span className="period-name">{t(period.labelKey, period.value)}</span>
                                        <span className="period-hours">{period.hours}</span>
                                    </button>
                                ))}
                            </div>

                            <div className="hour-select">
                                <label>{t('addActivity.specific_time')}</label>
                                <select 
                                    value={selectedTime || ''} 
                                    onChange={e => setSelectedTime(e.target.value || undefined)}
                                >
                                    <option value="">{t('addActivity.select_time')}</option>
                                    {HOURS.map(hour => (
                                        <option key={hour} value={hour}>{hour}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                {/* Quick Suggestions */}
                <div className="suggestions-section">
                    <label className="suggestions-label">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                        {t('addActivity.suggestions_label')}
                    </label>
                    <div className="suggestions-list">
                        {suggestions.map((suggestion, idx) => (
                            <button
                                key={idx}
                                className="suggestion-pill"
                                onClick={() => handleSuggestionClick(suggestion)}
                                disabled={isLoading}
                            >
                                <span className="suggestion-icon">{suggestion.icon}</span>
                                <span className="suggestion-text">{suggestion.text}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Submit Button */}
                <div className="add-activity-footer">
                    <button
                        className="add-with-ai-btn"
                        onClick={handleSubmit}
                        disabled={!description.trim() || isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className="loading-spinner" />
                                {t('addActivity.adding')}
                            </>
                        ) : (
                            <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 3l1.912 5.813L20 10l-4.562 2.5L17 18l-5-3.5L7 18l1.562-5.5L4 10l6.088-1.187L12 3z" />
                                </svg>
                                {t('addActivity.add_with_ai')}
                            </>
                        )}
                    </button>
                    <p className="footer-hint">
                        {t('addActivity.hint')}
                    </p>
                </div>
            </div>
        </div>
    );
}

